import React, { useEffect } from "react";
import { useForm } from "react-hook-form";

import { useUtilityStates } from "../useStates";

import Title from "../../LoanTermDeviation/Title";
import Button from "../../../components/atomic/Button";
import { SliderContainerType } from "../SliderContainer";
import Input from "../../../components/composed/FormElements/Input";

import { notify } from "../../../shared/context/AlertContext";
import GetAuthContext from "../../../shared/context/AuthContext";

import { LoanApplicationService } from "../../../services/LoanApplication";
import DropdownWithSearch from "../../../components/atomic/DropdownWithSearch";
import Label from "../../../components/atomic/Label";
import { CommonService } from "../../../services/Common";
import { get } from "../../../shared/services/Fetch";
import { ORG_IDS } from "../../../shared/utils/constants";
import {
  filterSourceEntityIDs,
  getAddUserError,
  sanitizeMobileInput,
  sanitizePanInput,
  shouldAcknowledgeDisqualification
} from "./CreateCustomer.utils";
import { MOBILE_NUMBER_LENGTH } from "./CreateCustomer.constants";
import DisqualificationAcknowledgeModal from "./DisqualificationAcknowledgeModal";
import { DisqualificationReason } from "../interface";
import ErrorTextBox from "../../../components/atomic/ErrorBox/ErrorTextBox";

import { getFormattedDate } from "@unified-dashboard/src/shared/utils/DateUtil";
import { getStoredProgrammeType } from "@unified-dashboard/src/shared/utils/platformAuth";

export default function CreateCustomer(props: SliderContainerType) {
  const { close } = props;
  const { fetchToken, userState } = GetAuthContext();
  const [loading, setLoading] = React.useState(false);
  const { setCustomerData, setSidebar } = useUtilityStates((state) => state);
  const [sourceEntityIDs, setSourceEntityIDs] = React.useState([]);
  const [selectedEntityID, setSelectedEntityID] = React.useState("");
  const [entityIDError, setEntityIDError] = React.useState("");
  const [disqualificationReasons, setDisqualificationReasons] = React.useState<
    DisqualificationReason[]
  >([]);
  const [showDisqualificationModal, setShowDisqualificationModal] =
    React.useState(false);
  const [submittedData, setSubmittedData] = React.useState<{
    mobile: string;
    pan?: string;
  }>({ mobile: "" });
  const [formResetKey, setFormResetKey] = React.useState(0);

  const isEducationLoan = getStoredProgrammeType() === "EDUCATION_LOAN";
  const isPFL = userState?.organizationID === ORG_IDS.PFL;
  const isPanMandatory = isPFL && isEducationLoan;

  const {
    register,
    setValue,
    watch,
    reset,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const panValue = watch("pan", "");

  const getSourceEntityID = async () => {
    let response;
    if (props?.pageName?.trim()) {
      response = await CommonService.getSourceEntityList({
        token: fetchToken(),
        pageName: props?.pageName
      });
      if (response.status && response.data) {
        const sourceEntityIDs: any = [];
        for (let index = 0; index < response.data.sourceList.length; index++) {
          sourceEntityIDs.push({
            value: response.data.sourceList[index].sourceEntityID,
            name: response.data.sourceList[index].sourceEntityName
          });
        }
        setSourceEntityIDs(sourceEntityIDs);
      } else {
        notify({
          title: "Error",
          text: response.error || "Something Went Wrong"
        });
      }
    }
  };

  useEffect(() => {
    getSourceEntityID();
  }, []);

  const generateWebLink = async (userid: string) => {
    const searchParams = new URLSearchParams();

    searchParams.append("userID", userid);
    searchParams.append("redirectURL", window.location.href);

    const resp = await get<{ url: string }>(
      `/masterDashboard/generateWebLink?${searchParams.toString()}`,
      props?.pageName
    );

    return resp.status && resp.data ? resp.data?.url : "";
  };

  /**
   * Clears the form after every completed attempt — success, duplicate, error,
   * or an abandoned disqualification popup — so the sidebar never holds stale
   * data (the journey redirect on success is not guaranteed either;
   * generateWebLink can return an empty link).
   *
   * Deliberately NOT called when the disqualification popup opens: the retry
   * behind its Confirm still needs `selectedEntityID` and the typed values.
   */
  const resetForm = () => {
    reset({ mobile: "", pan: "" });
    setSelectedEntityID("");
    setEntityIDError("");
    // DropdownWithSearch holds its selection internally; remount to clear it.
    setFormResetKey((previousKey) => previousKey + 1);
  };

  const createLead = async (
    data: { mobile: string; pan?: string },
    previousDisqualificationAcknowledged = false
  ) => {
    if (!selectedEntityID) {
      if (isPanMandatory) {
        setEntityIDError(`${getEntityFieldName()} is required.`);
      }
      return false;
    }

    setLoading(true);

    const response = (await LoanApplicationService?.addUser({
      pageName: props?.pageName,
      token: fetchToken(),
      payload: {
        mobile: data?.mobile || "",
        partnerCode: "dummy_partner_code", //TODO we are retrieving it from offer?.partner_code. Required on backend, need to replace the dummy value
        sourceEntityID: selectedEntityID,
        isPreApproved: false, // Passing false but not required on backend
        ...(isEducationLoan && data?.pan ? { pan: data.pan } : {}),
        ...(previousDisqualificationAcknowledged
          ? { previousDisqualificationAcknowledged: true }
          : {})
      },
      sourceEntityID: selectedEntityID
    })) as any;

    // The disqualification details normally come under `error`, but fall back
    // to the top level in case the backend sends them unwrapped.
    const addUserError = getAddUserError(
      response?.error ??
        (response?.disqualificationReasons ? response : undefined)
    );

    if (response?.status && response?.data) {
      notify({
        type: "success",
        title: "Success",
        text: "New lead is created."
      });

      resetForm();

      const journeyLink = await generateWebLink(response?.data?.userID);
      window.location.href = journeyLink;
    } else if (
      isPFL &&
      shouldAcknowledgeDisqualification(addUserError)
    ) {
      // The lead was disqualified earlier: ask the user to acknowledge the
      // reason codes and retry, instead of showing the generic error.
      setSubmittedData(data);
      setDisqualificationReasons(
        addUserError?.disqualificationReasons ?? []
      );
      setShowDisqualificationModal(true);
    } else if (response?.statusCode === 409) {
      const errorDetails = response?.error;
      
      notify({
        title: "Error",
        type: "error",
        text: (
          <>
            {errorDetails?.message && <p>{errorDetails.message}</p>}
            {errorDetails?.caseOwner && (
              <p>Case Owner: {errorDetails.caseOwner}</p>
            )}
            {errorDetails?.ownerEmail && (
              <p>Email ID: {errorDetails.ownerEmail}</p>
            )}
            {errorDetails?.dateOfLogin && (
              <p>
                Date of Login: {getFormattedDate(errorDetails.dateOfLogin, true, true)}
              </p>
            )}
            {errorDetails?.customerID && (
              <p>Customer ID: {errorDetails.customerID}</p>
            )}
          </>
        )
      });

      resetForm();
    } else {
      notify({
        title: "Error",
        type: "error",
        text:
          addUserError?.message ||
          "Could not create a new Lead"
      });

      resetForm();
    }

    setLoading(false);

  };

  const onSubmitHandler = async (data: { mobile: string; pan?: string }) =>
    createLead(data);

  const getLabel = () => {
    return ORG_IDS.PFL ? "Select DSA" : "Select Platform";
  };

  const getEntityFieldName = () => {
    return ORG_IDS.PFL ? "DSA" : "Platform";
  };

  return (
    <div className="relative min-h-full p-6 sm:p-10">
      <Title
        close={close}
        title="Create New Lead"
      />

      <div className="text-xs text-[#4C4C4D] font-normal">
        Ensure to enter the correct phone number of the Student (Primary
        Applicant)
      </div>
      <form
        className="mt-10"
        onSubmit={handleSubmit(onSubmitHandler)}
      >
        <fieldset>
          <Input
            requiredIcon
            type="text"
            label="Phone Number"
            placeholder="Enter Phone Number"
            register={register("mobile", {
              required: "Phone Number is required.",
              // Strip formatting that comes along with a pasted number,
              // otherwise `maxLength` truncates it into an invalid value.
              onChange: (event) =>
                setValue("mobile", sanitizeMobileInput(event?.target?.value), {
                  shouldValidate: true
                }),
              validate: {
                length: (value) => {
                  if (!value) return "Phone Number is required.";
                  if (value.length !== MOBILE_NUMBER_LENGTH)
                    return "Phone Number must be exactly 10 digits long.";
                  return true;
                },
                format: (value) => {
                  if (!/^[6-9]/.test(value))
                    return "Phone number must start with 6, 7, 8 or 9.";
                  else if (!/^\d+$/.test(value))
                    return "Phone number can only contain digits.";
                  return true;
                }
              }
            })}
            error={errors.mobile}
            errorText={errors?.mobile?.message as string}
          />
        </fieldset>

        {isEducationLoan && (
          <fieldset className="mt-4">
            <Label
              requiredIcon={isPanMandatory}
              name="pan"
              label="Pan Number"
            />
            <input
              type="text"
              {...register("pan", {
                validate: (value) => {
                  if (!value) return isPanMandatory ? "PAN Number is required." : true;
                  return (
                    /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value) ||
                    "Invalid PAN Number format."
                  );
                }
              })}
              value={panValue}
              placeholder="Enter PAN Number"
              onChange={(e) => {
                setValue("pan", sanitizePanInput(e.target.value), {
                  shouldValidate: true
                });
              }}
              className="block w-full rounded-md border-gray-200 sm:text-sm lg:text-xs px-4 py-2 placeholder:text-[#CECFCF]"
            />
            {errors?.pan?.message && (
              <ErrorTextBox text={errors?.pan?.message as string} />
            )}
          </fieldset>
        )}

        <fieldset>
          <div className="mt-4 w-full">
            <Label
              requiredIcon={isPanMandatory}
              name={"Select Platform"}
              label={getLabel()}
            ></Label>
            
            <DropdownWithSearch
              key={formResetKey}
              options={sourceEntityIDs || []}
              searchFunc={(text: string) => { 
                setSourceEntityIDs(filterSourceEntityIDs(sourceEntityIDs, text));
              }}
              styles={{
                container: "w-full"
              }}
              placeholder={getLabel()}
              onChange={(event: any) => {
                setSelectedEntityID(event?.value);
                setEntityIDError("");
              }}
            />
            {entityIDError && <ErrorTextBox text={entityIDError} />}
          </div>
        </fieldset>

        <fieldset className="w-full mt-6">
          <Button
            type="submit"
            color="primary"
            disabled={loading}
            className="w-full sm:w-auto"
          >
            Create Lead
          </Button>
        </fieldset>
      </form>

      <DisqualificationAcknowledgeModal
        open={showDisqualificationModal}
        reasons={disqualificationReasons}
        onCancel={() => {
          setShowDisqualificationModal(false);
          resetForm();
        }}
        onConfirm={() => {
          setShowDisqualificationModal(false);
          createLead(submittedData, true);
        }}
      />
    </div>
  );
}
